import Fuse from 'fuse.js';

import ngStorage from '../../common/wrappers/ngstorage';
import locationsCompactTpl from './locations-compact.tpl.html';
import locationsFullTpl from './locations-full.tpl.html';
import locationsMapTpl from './locations-map.tpl.html';

import angularSpinner from '../../common/wrappers/angular-spinner';
import gaMapsService from '../common/gamaps.service';
import geolocationModule, {GEOLOCATION_REQUESTED_EVENT} from '../common/location/geolocation.service';
import authServiceMod from '../auth/auth.service';
import scrollServiceMod from '../common/scroll.service';
import analyticsServices from '../analytics/analytics.service';

import locationCardModule from './location-card.component';
import locationRes from '../common/resources/location.resource';
import cityRes from '../common/resources/city.resource';
import personRes from '../common/resources/person.resource';
import serviceRes from '../common/resources/service.resource';
import locationAreaRes from '../common/resources/location-area.resource';
import {serviceFilter} from './location-filters';
import {LocationMap} from './location-map';

import flashMessageMod from '../../common/flashmessageservice/flashmessageservice.module';
import textfieldComponentMod from '../../common/material-components/textfields/textfield.component';
import normButtonComponentMod from '../../common/material-components/buttons/norm-button.component';
import tabBarComponentMod from '../../common/material-components/tabs/tab-bar.component';

import serviceSelector from '../common/components/service-selector.component';
import generalCardComponentMod from '../reservation/selections/general-card.component';

import './locations-widget.scss';

import _ from 'lodash';
import {
  elementIsInsideViewport,
  elementIsAboveViewport,
  noop
} from "../common/common.utils";
import LocationFilterState, { KEYS } from '../common/session-storage/location-filter-state';
import {combineMatches, doSearch, getSearchTerms} from "../common/search";

import LocationStorage from '../common/location/location-storage';

import {
  googleMapsSearchUrl
} from '../common/common.urls';

/**
 * Display this many locations more when `show more` clicked.
 * @type {Number}
 */
const DISPLAY_ADD = 18;

const ZOOM_TO_NEAR_MARKERS_AMOUNT = 3;

/**
 * How many locations are displayed at max when normal display limit is not used.
 * @type {number}
 */
const MAX_LOCATIONS = 150;

const FUSE_CONFIG = {
  id: 'change_id',
  includeScore: true,
  includeMatches: true,
  threshold: 0.3,
  location: 0,
  distance: 200,
  maxPatternLength: 32,
  minMatchCharLength: 3,
  keys: [
    {name: "name", weight: 0.9999},
    {name: "address.postal_area", weight: 0.8},
    {name: "address.street", weight: 0.6},
    {name: "address.postal_code", weight: 0.6},
  ]
};

/**
 * Standalone component to display filtered locations.
 */
class LocationsComponent {
  constructor(
    $scope,
    $log,
    $httpParamSerializer,
    $q,
    $timeout,
    $rootScope,
    $compile,
    $sessionStorage,
    $document,
    GaMaps,
    Analytics,
    AuthService,
    LocationResource,
    CityResource,
    LocationAreaResource,
    ServiceResource,
    PersonResource,
    FlashMessageService,
    GeolocationService,
    usSpinnerService,
    ScrollService,
    gettextCatalog,
  ) {
    'ngInject';

    this.$scope = $scope;
    this.$log = $log;
    this.$httpParamSerializer = $httpParamSerializer;
    this.$q = $q;
    this.$timeout = $timeout;
    this.$sessionStorage = $sessionStorage;
    this.$document = $document;
    this.Analytics = Analytics;
    this.AuthService = AuthService;
    this.FlashMessageService = FlashMessageService;
    this.GeolocationService = GeolocationService;
    this.LocationResource = LocationResource;
    this.CityResource = CityResource;
    this.LocationAreaResource = LocationAreaResource;
    this.ServiceResource = ServiceResource;
    this.PersonResource = PersonResource;
    this.usSpinnerService = usSpinnerService;
    this.ScrollService = ScrollService;
    this.gettextCatalog = gettextCatalog;
    this.locationFilterState = new LocationFilterState($log, $sessionStorage);

    /**
     * Is locations displayed inside modal and should be displayed in more compact manner.
     * @type {boolean}
     */
    this.compactMode = false;

    /**
     * Is only location map displayed
     * @type {boolean}
     */
    this.mapMode = false;

    /**
     * Distance order selected?
     * @type {Boolean}
     */
    this.distanceOrder = false;

    /**
     * Distances for locations set?
     * @type {Boolean}
     */
    this.distancesReady = false;

    /**
     * Number of locations initially fetched from the server.
     * @type {Number}
     */
    this.initialLocationCount = 0;

    /**
     * Loading locations from the backend?
     * @type {Boolean}
     */
    this.loading = false;

    /**
     * FuseJS search object; populated when data is received.
     * @type {Fuse<any, Fuse.FuseOptions<any>> | Fuse | null}
     */
    this.fuseSearch = null;

    /**
     * Locations retrieved from the backend for display.
     * @type {Array.<Location>}
     */
    this.locations = [];

    /**
     * Locations are searched by this text.
     * @type {String}
     */
    this.locationSearchText = null;

    this.locationMap = new LocationMap($q, $timeout, $rootScope, $compile, GaMaps, (id) => {
      this.LocationResource.getOne(id)
      .then(location => this.onLocationSelected(location));
    });

    /**
     * Locations filtered in the template.
     * @type {Array.<Location>}
     */
    this.filteredLocations = [];

    this.mapVisible = false;

    /**
     * Options for the order dropdown.
     */
    this.orderOptions = [
      {
        label: this.gettextCatalog.getString('Aakkosjärjestys'),
        value: false
      },
      {
        label: this.gettextCatalog.getString('Etäisyysjärjestys'),
        value: true
      }
    ];

    /**
     * Locations are filtered by this given area, if set.
     * @type {Number}
     */
    this.areaFilterValue = null;

    /**
     * Locations are filtered by this given city, if set.
     * @type {String}
     */
    this.cityFilterValue = null;

    /**
     * Service that is used for filtering, if given.
     * @type {?Service}
     */
    this.serviceFilter = null;

    this.showInfo = false;
    this.locationInfoCallback = null;

    /**
     * Provided service id to filter by
     * @type {?Number}
     */
    this.serviceId = null;
    /**
     * Provided person id to filter by
     * @type {?Number}
     */
    this.personId = null;

    /**
     * Provided city id to filter by
     * @type {?Number}
     */
    this.cityId = null;

    this.person = null;
    this.service = null;

    /**
     * Used to trigger a reset of location selector component. Bind this to the
     * location selector's selection to it, and assign a (new) object to this
     * to cause a reset in the selector. Actual value is discarded.
     * @type {?Object}
     */
    this.locationSelection = null;

    this._userNotify = user => $timeout(() => this._setUser(user));
    this.showFavourites = false;
    this.favouriteLocations = [];

    /**
     * Possible active location. Active selection will be displayed differently.
     * @type {Location}
     */
    this.activeLocation = null;

    /**
     * If user has requested positioning but position was unavailable, disable positioning button.
     * @type {boolean}
     */
    this.geolocationDisabled = false;

    /**
     * Button is also hidden when geolocation has been fetched
     * @type {boolean}
     */
    this.geolocationFetched = false;

    /**
     * Are locations being loaded. Results are not shown before loading is finished.
     * @type {boolean}
     */
    this.loading = true;

    /**
     * Display styles for location list given to tab bar
     * @type {Array}
     */
    this.displayStyles = [];

    this.locationStorage = new LocationStorage($log, $sessionStorage);

    /**
     * Show link to reservation with prefilled location filter
     * @type {boolean}
     */
    this.showReserveLink = false;

    /**
     * Are cities displayed in the location list and search results
     * @type {boolean}
     */
    this.showCities = false;

    /**
     * Is floating "Show nearby" button visible at the bottom of the screen
     * @type {boolean}
     */
    this.hideFloatingNearbyButton = false;

    /**
     * Custom text for select button of infoview box of map component
     * @type {String}
     */
    this.locationSelectCustomText = null;

    /**
     * Link to Google Maps search
     */
    this.googleMapsUrl = null;
  }

  $onInit() {
    if (this.mapMode) {
      this.mapVisible = true;
      this.distanceOrder = false;
    } else {
      this.mapVisible = this.locationFilterState.get(KEYS.MAP_VISIBLE);
    }

    if (this.cityId) {
      this.cityId = parseInt(this.cityId, 10);
      this.showCities = false;
    }

    this.displayStyles = [
      {
        title: this.gettextCatalog.getString('Lista'),
        active: !this.mapVisible
      },
      {
        title: this.gettextCatalog.getString('Kartta'),
        active: this.mapVisible
      }
    ];

    this.AuthService.subscribeUser(this._userNotify);

    if (this.personId != null) {
      this.person = this.PersonResource.cache.getOneCached(this.personId);
      this.$log.debug("Got person id in locations. Match person:", this.person);
    }

    if (this.serviceId != null) {
      this.service = this.ServiceResource.cache.getOneCached(this.serviceId);
      this.$log.debug("Got service id in locations. Match service:", this.service);
    }
    const prePromises = [];
    prePromises.push(this.AuthService.getUser());

    if (this.GeolocationService.userLocation != null) {
      this.distancesReady = true;
    }
    if (this.locationStorage.getGeolocationCity()) {
      // Safe to fetch "cached location"
      prePromises.push(this.GeolocationService.getLocation());
    }

    this.$q.all(prePromises).then(([user, location]) => {
      this._setUser(user);
      if (location) {
        this.$log.debug("Resolved cached coordinates", location);
      }
    })
    .catch(noop)
    .then(() => this.updateLocations())
    .finally(() => {
      const mobileShowNearbyLinkElement = document.getElementById('mobile-locations-show-nearby-link');

      this.$document.on('scroll', () => this.hideOrShowFloatingNearbyButton(mobileShowNearbyLinkElement));
    });
  }

  hideOrShowFloatingNearbyButton(mobileShowNearbyLinkElement) {
    if (mobileShowNearbyLinkElement) {
      const insideViewport = elementIsInsideViewport(mobileShowNearbyLinkElement);
      const aboveViewport = elementIsAboveViewport(mobileShowNearbyLinkElement);
      if ((insideViewport || aboveViewport) && !this.hideFloatingNearbyButton) {
        this.hideFloatingNearbyButton = true;
        this.$scope.$apply();
      } else if (!insideViewport && !aboveViewport && this.hideFloatingNearbyButton) {
        this.hideFloatingNearbyButton = false;
        this.$scope.$apply();
      }
    }
  }

  $onDestroy() {
    this.AuthService.unsubscribeUser(this._userNotify);
    this.$document.off('scroll');
  }

  clearFilters() {
    this.locationSearchText = null;
    this.cityFilterValue = null;
    this.areaFilterValue = null;
    this.distanceOrder = false;
  }

  updateLocations() {
    // Clear locations when loading
    this.locations = [];
    this.loading = true;

    const filters = this.serviceFilter ? {serviceFilter: this.serviceFilter} : {};
    this.$q.all([
      this.CityResource.getAll(),
      this.LocationResource.getMany(filters)
    ])
    .then(([cityData, locationData]) => {
      this.locations = this.processLocations(locationData.list);

      // Cities are only included when they are explicitly wanted
      if (this.showCities) {
        // just pick cities with more than 1 location
        this.cities = this.processLocations(cityData.list.filter(c => c.locations.length > 1));
        this.locations.push(...this.cities);
      }
      if (this.cityId) {
        this.locations = this.locations.filter((loc) => loc.city === this.cityId);

        if (this.mapMode) {
          const city = cityData.list.find((c) => c.id === this.cityId)
          this.googleMapsUrl = googleMapsSearchUrl(city.name);
        }
      }

      this.fuseSearch = new Fuse(this.locations, FUSE_CONFIG);
      this.distanceOrder = false;
      this.distancesReady = false;
      if (!this.initialLocationCount) {
        this.initialLocationCount = this.locations.length;
      }

      if (this.mapVisible) {
        this.locationMap.initialize().then(() => {
          this.updateSearches();
        });
      } else {
        this.updateSearches();
      }
    })
    .catch((error) => {
      this.$log.error("Failed to load a location: ", error);
    })
    .finally(() => {
      this.loading = false;
      if (this.GeolocationService.userLocation != null) {
        this.onLocationReceived();
      }
    });
  }

  processLocations(locations) {
    return locations.map(location => {
      location = location.copy();

      location.hasService = true;
      location.hasOffline = false;
      location.hasPerson = true;

      if (this.service != null) {
        location.hasService = location.services != null && location.services.includes(this.service.id);
        location.hasOffline = location.other_services != null && location.other_services.includes(this.service.id);
      }
      if (this.person != null) {
        location.hasPerson = location.includes(this.person.locations);
      }

      // Generate mismatch errors
      location.mismatches = [];
      if (!location.hasService) {
        location.mismatches.push(this.gettextCatalog.getString('Toimipiste ei tarjoa valittua palvelua <strong>"{{service}}"</strong>.', {
          service: this.service.name
        }))
      }
      if (!location.hasPerson) {
        location.mismatches.push(this.gettextCatalog.getString('Asiantuntija <strong>"{{person}}"</strong> ei työskentele toimipisteessä.', {
          person: this.person.fullName()
        }))
      }
      if (location.hasOffline) {
        const notAvailableText = this.gettextCatalog.getString('Toimipisteeseen ei voi varata aikaa verkossa palvelulle <strong>"{{service}}"</strong>.', {
          service: this.service.name
        });
        const reserveByPhoneText = this.gettextCatalog.getString('Varaa aika soittamalla <a class="link-no-style" href="tel:+358104003400"><strong>010 400 3400</strong></a>.');
        location.mismatches.push(`${notAvailableText} ${reserveByPhoneText}`);
      }

      return location;
    })
  }

  updateFiltering() {
    if (this.mapVisible) {
      if (this.distanceOrder) {
        this.locationMap.updateMarkers(this.filteredLocations, true, this.locationSelectCustomText, ZOOM_TO_NEAR_MARKERS_AMOUNT);
      } else {
        this.locationMap.updateMarkers(this.filteredLocations, true, this.locationSelectCustomText);
      }
    }

    this.updateGroups();
  }

  updateSearches() {
    // Update locations without changing the array reference
    this.filteredLocations.length = 0;
    const newLocations = this.filterLocations(this.locations);
    this.filteredLocations.push(...newLocations);
    this.updateFiltering();
  }

  updateGroups() {
    let baseTitle = "";
    let titleAddition = "";

    if (this.service != null || this.person != null || this.showFavourites) {
      /// Toimipisteet asiantuntijalle X
      baseTitle = this.gettextCatalog.getString("Toimipisteet")
    }
    if (this.person != null) {
      /// Toimipisteet asiantuntijalle X
      titleAddition += `${this.gettextCatalog.getString("asiantuntijalle")} <strong>${this.person.fullName()}</strong>`;
    }
    if (this.service != null) {
      /// Toimipisteet palvelulle X
      titleAddition += ` ${this.gettextCatalog.getString("palvelulle")} <strong>${this.service.name}</strong>`;
      titleAddition = titleAddition.trim();
    }

    const onlineTitle = `${baseTitle} ${titleAddition}`.trim();

    this.locationGroups = [
      {
        name: "onlineLocations",
        title: onlineTitle,
        locations: this.filteredLocations,
        showNotFound: true,
        maxDisplay: this.compactMode ? MAX_LOCATIONS : Number.MAX_SAFE_INTEGER,
      }
    ];

    if (this.showFavourites) {
      let filteredFavourites = this.favouriteLocations.slice();

      if (this.service != null) {
        filteredFavourites = serviceFilter(filteredFavourites, this.service.id);
        this.$log.debug("Favourites after service", filteredFavourites, this.service.id);
      }

      if (this.person != null) {
        filteredFavourites = filteredFavourites.filter(location => this.person.locations.includes(location.id));
        this.$log.debug("Favourites after person", filteredFavourites, this.person);
      }


      this.locationGroups.unshift({
        name: "favouriteLocations",
        title: this.gettextCatalog.getString('Tutut toimipisteet'),
        locations: filteredFavourites,
        showNotFound: false,
        maxDisplay: this.locationFilterState.get(KEYS.MAX_DISPLAYS_FAVOURITE_LOCATIONS),
      });
    }

    this.$log.debug("Final locationGroups", this.locationGroups);
  }

  _locationSort(distanceOrder) {
    return (l, r) => {
      if (distanceOrder) {
        return l.score - r.score || l.distance - r.distance;
      } else {
        // If l and r have same name, attempt to sort by determining if l is city
        return l.score - r.score || l.name.localeCompare(r.name, 'fi') || (l.isArea() ? -1 : 1);
      }
    }
  }

  filterLocations(locations) {
    const searchTerms = getSearchTerms(this.locationSearchText);
    let searchResults = doSearch(searchTerms, this.fuseSearch);

    // If there were no search results and search was used, exit now as we don't need to show any persons
    // Just an error message with an empty person list
    if (searchTerms.length > 0 && Object.keys(searchResults).length === 0) {
      return locations.slice(0,0);
    }

    // Case 2 no search terms
    else if (searchTerms.length === 0) {
      locations = locations.filter(l => l.hasPerson && l.hasService)
        // Hide cities when order is by distance
        .filter(l => !(this.distanceOrder && l.isArea()));
      for (const location of locations) {
        location.score = location.hasOffline * 0.2 + !location.hasPerson * 0.4 + !location.hasService * 0.3;
        location.matches = [];
      }
      return locations.sort(this._locationSort(this.distanceOrder));
    }

    const resultLocations = Object.keys(searchResults).map(locationId => {
      // Locations are searched from set of the same locations so there has to be a match
      const location = locations.find(p=> p.change_id === parseInt(locationId, 10));

      const result = searchResults[location.change_id];

      if (location.isArea()) {
        // Prefer cities during search
        location.score = result.score * 0.8;
        return location;
      }

      // Calculate final scores and show matches
      // score personnel based on matches in service and location if set
      location.score = location.hasOffline * 0.2 + !location.hasPerson * 0.4 + !location.hasService * 0.3;

      location.score += result.score;
      location.matches = combineMatches(result.matches);

      // Add score (worsen result) for each complete search miss
      // If 3 terms are searched and person has 2 matches, they will get 1 penalty
      // This is the same score as getting complete mismatch from Fuse search
      location.score += searchTerms.length - result.matchCount;

      return location;
    });

    return resultLocations.sort(this._locationSort(this.distanceOrder));
  }

  onAreaFilterSet(selection) {
    if (selection == null) {
      this.areaFilterValue = selection;
      return;
    }
    else {
      this.clearFilters();
      this.areaFilterValue = selection;
    }

    // This is needed in order to get the up-to-date (intermediate) array of filtered
    // locations when a new area is selected.
    this.$timeout(() => {
      this.updateFiltering();
    });
    this.Analytics.postEvent('toimipisteet', 'dropdown', 'alue');
  }

  onCityFilterSet(selection) {
    if (selection == null) {
      this.cityFilterValue = selection;
      return;
    }
    else {
      this.clearFilters();
      this.cityFilterValue = selection;
    }

    this.updateFiltering();
    this.Analytics.postEvent('toimipisteet', 'dropdown', 'kaupunki');
  }

  onServiceFilterSet(service) {
    this.clearFilters();
    if (service) {
      this.serviceFilter = service.id;
    } else {
      this.serviceFilter = null;
    }

    this.updateLocations();
  }

  onLocationSearchTextChanged() {
    this.updateSearches();

    this.Analytics.postEvent('toimipisteet', 'haku', '-');
  }

  onLocationSelected(location) {
    if (_.isFunction(this.locationSelectedCallback)) {
      this.locationSelectedCallback({location});
    }
  }

  onOrderSet() {
    this.updateFiltering();
    this.Analytics.postEvent('toimipisteet', 'jarjestys', '-');
  }

  showNearby() {
    this.$scope.$broadcast(GEOLOCATION_REQUESTED_EVENT);
    this.loadingLocation = true;
    this.GeolocationService.getLocation()
      .catch(() => {
        this.$log.debug("Geolocation fetch failed");
      })
      .finally(() => {
        this.onLocationReceived();
        this.loadingLocation = false;
      });
    this.Analytics.postEvent('toimipisteet', 'paikannus', 'paikannus-nappi');
  }

  showNearbyOrCitiesAndScroll() {
    this.ScrollService.navigateToId("locationSearchInput");
    this.toggleOrder();
  }

  toggleOrder() {
    this.distanceOrder = !this.distanceOrder;
    if (this.distanceOrder) {
      this.showNearby();
    } else {
      this.updateSearches();
    }
  }

  onLocationReceived() {

    if (this.GeolocationService.userLocation != null) {
      this.clearFilters();

      this.locations = this.GeolocationService.calculateDistances(this.locations);
      this.distancesReady = true;
      this.distanceOrder = true;
      this.geolocationFetched = true;

      this.updateSearches();
    }
    else {
      this._showLocationError();
    }
    if (this.GeolocationService.locationUnavailable) {
      this.geolocationDisabled = true;
      this.distanceOrder = false;
    }

    this.Analytics.postEvent('toimipisteet', 'paikannus', 'dropdown');
  }

  _showLocationError() {
    this.FlashMessageService.addMessage(this.gettextCatalog.getString(
      'Emme voineet paikantaa sinua - etsi toimipistettä tai valitse listalta.'), 'error');
  }

  onMapViewClicked() {
    this.mapVisible = true;
    this.locationFilterState.save(KEYS.MAP_VISIBLE, this.mapVisible);
    this.locationMap.initialize().then(() => {
      this.updateFiltering();
    });

    this.Analytics.postEvent('toimipisteet', 'kartta', '-');
  }

  onCardViewClicked() {
    this.mapVisible = false;
    this.locationFilterState.save(KEYS.MAP_VISIBLE, this.mapVisible);
  }

  displayMore(locationGroup) {
    locationGroup.maxDisplay += DISPLAY_ADD;
    locationGroup.maxDisplay = Math.min(locationGroup.locations.length, locationGroup.maxDisplay);

    // save display more states
    const path = `${KEYS.MAX_DISPLAYS}.${locationGroup.name}`;
    this.locationFilterState.save(path, locationGroup.maxDisplay);
  }

  showLocations(locationGroup) {
    locationGroup.hidden = false;
  }

  onSearchEnter() {
    // Scroll down so that the search box is at the top and hopefully first locations cards are visible
    this.ScrollService.navigateToId("locationSearchInput", 75);
  }

  displaySelected(index) {
    if (index === 1) {
      this.onMapViewClicked();
    }
    else {
      //this.$timeout(() => this.onCardViewClicked());
      this.onCardViewClicked();
    }
  }


  _setUser(user) {  // eslint-disable-line no-unused-vars
    // Why is the only use for "user" in "_setUser" to log it is beyond me
    this.favouriteLocations.length = 0;
    this.showFavourites = false;

    return this.$q.when([]);
  }
}

const config = {
  templateUrl: function($element, $attrs, $log) {
    // Direct templateUrl so that transclude works
    $log.debug('Determining locations component template to be used', $attrs.compactMode);

    // Attrs are unevaluated.
    if($attrs.compactMode === "true") {
      return locationsCompactTpl;
    } else if ($attrs.mapMode === "true") {
      return locationsMapTpl;
    } else {
      return locationsFullTpl;
    }
  },
  controller: LocationsComponent,
  transclude: true,
  bindings: {
    compactMode: '<', // A more compact layout if displayed inside the location modal
    mapMode: '<?', // Layout showing only map
    locationSelectedCallback: '&',
    showInfo: '<?',
    locationInfoCallback: '&?',
    serviceId: '<?',
    service: '<?',
    personId: '<?',
    person: '<?',
    cityId: '<?',
    activeLocation: '<?',
    showReserveLink: '<?',
    showCities: '<?',
    locationSelectCustomText: '<?',
  }
};

const mod = angular.module('app.locations', [
    ngStorage, angularSpinner, locationAreaRes, locationRes, personRes, serviceRes, cityRes,
    serviceSelector, gaMapsService, geolocationModule, authServiceMod, locationCardModule, generalCardComponentMod,
    flashMessageMod, textfieldComponentMod, normButtonComponentMod, tabBarComponentMod,
    scrollServiceMod, analyticsServices])
  .component('locations', config)
;

export default mod.name;



// WEBPACK FOOTER //
// ./src/app/locations/locations.component.js